import { DOM_SELECTORS, EVENTS } from './constants';
import { InfraIoError } from './errors';
import type { CheckoutOptions } from './types';

const OVERLAY_STYLE = `
  position:fixed;inset:0;z-index:2147483647;
  background:rgba(0,0,0,0.55);
  display:flex;align-items:center;justify-content:center;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
`;

const MODAL_STYLE = `
  background:#fff;border-radius:16px;
  width:min(480px,96vw);height:min(680px,92vh);
  display:flex;flex-direction:column;
  overflow:hidden;box-shadow:0 24px 64px rgba(0,0,0,0.22);
  position:relative;
`;

const HEADER_STYLE = `
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 16px;border-bottom:1px solid #f0f0f0;flex-shrink:0;
`;

const CLOSE_BTN_STYLE = `
  background:none;border:none;cursor:pointer;
  width:30px;height:30px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  color:#888;font-size:18px;line-height:1;
  transition:background .15s;
`;

const IFRAME_STYLE = `
  width:100%;flex:1;border:none;display:block;
`;

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Opens the checkout iframe overlay.
 * Returns a `close()` function so the SDK can programmatically dismiss it.
 */
export function openPopup(
  publicKey: string,
  checkoutBaseURL: string,
  options: CheckoutOptions,
): () => void {
  const parentOrigin = window.location.origin === 'null' ? '*' : window.location.origin;

  const checkoutPageURL =
    options.checkoutUrl ?? `${checkoutBaseURL}/${encodeURIComponent(options.sessionId)}`;

  // Parse + merge params safely. `checkoutPageURL` may already contain a query
  // string from the API; naive concat would break the URL.
  let checkoutUrlObj: URL;
  try {
    checkoutUrlObj = new URL(checkoutPageURL);
  } catch {
    throw new InfraIoError('invalid_request_error', `Invalid checkoutUrl: ${checkoutPageURL}`);
  }

  const checkoutOrigin = checkoutUrlObj.origin;
  checkoutUrlObj.searchParams.set('pk', publicKey);
  checkoutUrlObj.searchParams.set('parentOrigin', parentOrigin);
  const url = checkoutUrlObj.toString();

  // ── Build DOM ──────────────────────────────────────────────────────────────
  const overlay = document.createElement('div');
  overlay.setAttribute('style', OVERLAY_STYLE);
  overlay.setAttribute(DOM_SELECTORS.OVERLAY, '');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'infraio-checkout-title');

  const modal = document.createElement('div');
  modal.setAttribute('style', MODAL_STYLE);

  const header = document.createElement('div');
  header.setAttribute('style', HEADER_STYLE);

  const brand = document.createElement('span');
  brand.id = 'infraio-checkout-title';
  brand.textContent = 'Checkout';
  brand.setAttribute('style', 'font-size:14px;font-weight:600;color:#111;');

  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('style', CLOSE_BTN_STYLE);
  closeBtn.setAttribute('type', 'button');
  closeBtn.innerHTML = '&#x2715;';
  closeBtn.setAttribute('aria-label', 'Close checkout');
  closeBtn.onmouseenter = () => (closeBtn.style.background = '#f5f5f5');
  closeBtn.onmouseleave = () => (closeBtn.style.background = 'none');

  header.appendChild(brand);
  header.appendChild(closeBtn);

  const iframe = document.createElement('iframe');
  iframe.src = url;
  iframe.setAttribute('style', IFRAME_STYLE);
  // `payment` for potential Payment Request API; `clipboard-write` so the
  // iframe's copy-address button works inside the sandboxed frame.
  iframe.setAttribute('allow', 'payment; clipboard-write');
  iframe.setAttribute('title', 'Infraio Checkout');

  modal.appendChild(header);
  modal.appendChild(iframe);
  overlay.appendChild(modal);

  // ── Body scroll-lock ───────────────────────────────────────────────────────
  const previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  // ── Focus management ──────────────────────────────────────────────────────
  // Restore focus to whatever was focused before the overlay opened.
  const previouslyFocused = document.activeElement as HTMLElement | null;

  // ── Cleanup ────────────────────────────────────────────────────────────────
  // Returns true the first call, false on every subsequent call. Callers use
  // the return value to guarantee only ONE terminal callback fires per session
  // — otherwise racing sources (overlay click + success postMessage; timeout +
  // success) could invoke both onCancel/onError and onSuccess on the merchant.
  let closed = false;
  function cleanup(): boolean {
    if (closed) return false;
    closed = true;
    window.removeEventListener('message', onMessage);
    document.removeEventListener('keydown', onKey);
    if (loadTimer !== null) clearTimeout(loadTimer);
    document.body.style.overflow = previousOverflow;
    if (document.body.contains(overlay)) {
      document.body.removeChild(overlay);
    }
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      try {
        previouslyFocused.focus();
      } catch {
        /* noop */
      }
    }
    return true;
  }

  function emitError(code: 'iframe_load_error' | 'iframe_timeout_error', message: string) {
    if (!cleanup()) return;
    options.onError?.(new InfraIoError(code, message));
  }

  // ── Load-timeout watchdog ─────────────────────────────────────────────────
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  let loaded = false;
  let loadTimer: ReturnType<typeof setTimeout> | null = null;
  if (timeoutMs > 0) {
    loadTimer = setTimeout(() => {
      if (loaded) return;
      emitError(
        'iframe_timeout_error',
        `Checkout iframe did not load within ${timeoutMs}ms.`,
      );
    }, timeoutMs);
  }
  iframe.addEventListener('load', () => {
    loaded = true;
    if (loadTimer !== null) {
      clearTimeout(loadTimer);
      loadTimer = null;
    }
  });
  // Most modern browsers don't fire iframe.onerror for navigation errors
  // (they load their own error page), but keep it for completeness.
  iframe.addEventListener('error', () => {
    emitError('iframe_load_error', 'Checkout iframe failed to load.');
  });

  // ── postMessage handler ────────────────────────────────────────────────────
  function onMessage(event: MessageEvent) {
    const { type, sessionId } = (event.data ?? {}) as {
      type?: string;
      sessionId?: string;
    };

    // Only react to our event types — ignore unrelated postMessage traffic
    // (wallet extensions, analytics SDKs, etc).
    if (type !== EVENTS.SUCCESS && type !== EVENTS.CANCEL) return;

    // Only accept messages from the checkout origin we actually opened.
    if (event.origin !== checkoutOrigin) {
      console.warn('[InfraIo] dropped postMessage — origin mismatch', {
        received: event.origin,
        expected: checkoutOrigin,
        type,
      });
      return;
    }

    if (sessionId !== options.sessionId) {
      console.warn('[InfraIo] dropped postMessage — sessionId mismatch', {
        received: sessionId,
        expected: options.sessionId,
        type,
      });
      return;
    }

    if (type === EVENTS.SUCCESS) {
      if (!cleanup()) return;
      options.onSuccess?.({ sessionId });
    } else if (type === EVENTS.CANCEL) {
      if (!cleanup()) return;
      options.onCancel?.();
    }
  }

  // ── Keyboard: ESC closes; basic focus-trap so Tab stays inside modal ──────
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (!cleanup()) return;
      options.onCancel?.();
      return;
    }
    if (e.key === 'Tab') {
      // With only the close button + iframe as focusable elements, keep focus
      // pinned to the close button to avoid escaping into the host page.
      e.preventDefault();
      closeBtn.focus();
    }
  }

  closeBtn.addEventListener('click', () => {
    if (!cleanup()) return;
    options.onCancel?.();
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      if (!cleanup()) return;
      options.onCancel?.();
    }
  });

  window.addEventListener('message', onMessage);
  document.addEventListener('keydown', onKey);
  document.body.appendChild(overlay);
  // Focus the close button so Tab doesn't start by escaping the modal.
  closeBtn.focus();

  return cleanup;
}
